import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, CalendarClock, ShieldCheck } from 'lucide-react';
import { CLOSURE_NOTICE_DAYS, DELETION_GRACE_DAYS } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ConsequencePreview } from './ConsequencePreview';
import { isValidTotp, TotpField } from './OwnerConfirm';
import type { ClosureFacts } from './TenantClosurePanel';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export function TenantClosurePage() {
  const { tenantId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), `/owner/tenants/${tenantId}?tab=data`);

  const tenant = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => get<ClosureFacts>(`/platform/tenants/${tenantId}`),
    enabled: Boolean(tenantId),
  });

  if (!tenant.data) {
    return (
      <FormScreen title="Close this facility" icon={Archive} backTo={returnTo}>
        <RecordState error={tenant.error} />
      </FormScreen>
    );
  }

  return <ClosureForm key={tenant.data.id} facts={tenant.data} returnTo={returnTo} />;
}

function ClosureForm({ facts, returnTo }: { facts: ClosureFacts; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [noticeDays, setNoticeDays] = useState(String(CLOSURE_NOTICE_DAYS));
  const [graceDays, setGraceDays] = useState(String(DELETION_GRACE_DAYS));
  const [adminsOnly, setAdminsOnly] = useState(false);
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const demo = facts.environment === 'demo';
  const notice = Number.parseInt(noticeDays, 10);
  const grace = Number.parseInt(graceDays, 10);

  const schedule = useMutation({
    mutationFn: () =>
      post(`/platform/tenants/${facts.id}/closure`, {
        totpCode,
        reason: reason.trim(),
        noticeDays: notice,
        graceDays: grace,
        access: adminsOnly ? 'admins' : 'everyone',
        confirmSlug: typed.trim(),
      }),
    onSuccess: () => {
      toast.success('Closure scheduled');
      void queryClient.invalidateQueries({ queryKey: ['tenant', facts.id] });
      void queryClient.invalidateQueries({ queryKey: ['tenant-exports', facts.id] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const slugMatches = typed.trim().toLowerCase() === facts.slug.toLowerCase();
  const daysValid = demo || (notice >= 1 && notice <= 365 && grace >= 1 && grace <= 60);
  const ready = slugMatches && daysValid && reason.trim().length >= 10 && isValidTotp(totpCode);
  const dirty =
    !schedule.isSuccess &&
    (noticeDays !== String(CLOSURE_NOTICE_DAYS) || graceDays !== String(DELETION_GRACE_DAYS) || adminsOnly || typed !== '' || reason !== '' || totpCode !== '');

  return (
    <FormScreen
      title={demo ? 'Delete this demo facility' : 'Close this facility'}
      description={facts.slug}
      icon={Archive}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        schedule.mutate();
      }}
    >
      <FormSection title="What happens" icon={Archive} columns={1}>
        <Alert variant="warning">
          {demo
            ? 'A demo has no notice period: it is deleted after 24 hours unless you cancel.'
            : `Everyone can still sign in, read, print and download during the notice. After it, sign-in stops and the account is deleted when the grace ends — only once its data was delivered or a waiver was recorded.${
                facts.deploymentMode === 'LOCAL' ? ' This facility runs its own server: its records stay there; only the online copy is removed.' : ''
              }`}
        </Alert>
        <ConsequencePreview tenantId={facts.id} />
      </FormSection>

      {!demo && (
        <FormSection title="Timing" icon={CalendarClock}>
          <Field label="Notice (days)">
            <Input inputMode="numeric" value={noticeDays} onChange={(event) => setNoticeDays(event.target.value.replace(/\D/g, '').slice(0, 3))} />
          </Field>
          <Field label="Grace before deletion (days)">
            <Input inputMode="numeric" value={graceDays} onChange={(event) => setGraceDays(event.target.value.replace(/\D/g, '').slice(0, 2))} />
          </Field>
          <Checkbox
            className="sm:col-span-2"
            checked={adminsOnly}
            onCheckedChange={setAdminsOnly}
            label="Only administrators may sign in during the notice"
            description="For fraud or security cases. By default everyone keeps read access."
          />
        </FormSection>
      )}

      <FormSection title="Confirm" icon={ShieldCheck} columns={1}>
        <Field label={`Type ${facts.slug} to confirm`} required>
          <Input value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" />
        </Field>
        <Field label="Reason" required hint="At least 10 characters. Recorded in the platform audit trail — a ticket number is ideal.">
          <Input value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
        <TotpField value={totpCode} onChange={setTotpCode} />
        {error && <Alert variant="destructive">{error}</Alert>}
      </FormSection>

      <FormActions
        submitLabel={demo ? 'Schedule deletion' : 'Schedule closure'}
        loading={schedule.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
        destructive
        bleed={false}
      />
    </FormScreen>
  );
}
